import "../../candidate-intelligence/results/styles.css";
import type {
  CandidateTimeline,
  CandidateTimelineRound,
  RoundEvaluationSummary,
  RoundOutcome,
} from "@shared/labs/level-assessment-candidate";
import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  ChevronLeft,
  Info,
  Plus,
  Undo2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { ActiveModelProvider, useActiveModel } from "../active-model-context";
import { LevelWhisker } from "../results/level-whisker";
import { C, DM, MONO, UI } from "../results/ui-tokens";
import { fmtDate, Muted } from "../sets/ui";
import { AddRoundPanel } from "./add-round-panel";
import {
  Avatar,
  CandidateKeyframes,
  CandidateStatusChip,
  ConfidenceChip,
  DisciplineChip,
  ErrorText,
  GhostButton,
  LevelPill,
  ModalShell,
  MovementChip,
  OverlayActions,
  OverlayHeader,
  PacketChip,
  PrimaryButton,
  RoundStatusChip,
  Skeleton,
  VIOLET_BD,
  VIOLET_BG,
} from "./candidate-ui";
import { ConvergenceLens } from "./lenses/convergence";
import { EvidenceLens } from "./lenses/evidence";
import { OverviewLens } from "./lenses/overview";
import { TrajectorySparkline } from "./trajectory-sparkline";

/** Fast enough that a settling score appears near-instantly, calm enough not to hammer. */
const POLL_MS = 2500;

export default function LevelAssessmentCandidateTimeline() {
  return (
    <ActiveModelProvider>
      <TimelineView />
    </ActiveModelProvider>
  );
}

/** The current (effective) evaluation and the round it came from — the latest
 *  round that produced an evaluation (scored OR abstention). Carry-forward /
 *  evaluating rounds don't reset it. */
function effectiveFrom(data: CandidateTimeline): number | null {
  for (let i = data.rounds.length - 1; i >= 0; i--) {
    if (data.rounds[i].currentEvaluation) return data.rounds[i].sequence;
  }
  return null;
}

function TimelineView() {
  const [, params] = useRoute("/labs/level-assessment/candidates/:candidateId");
  const candidateId = params?.candidateId ?? "";
  const { model } = useActiveModel();

  const [data, setData] = useState<CandidateTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [lens, setLens] = useState<CaseLens>("overview");
  const [statusBusy, setStatusBusy] = useState(false);
  const [undoTarget, setUndoTarget] = useState<CandidateTimelineRound | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const pollRef = useRef<{ cancelled: boolean } | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }): Promise<CandidateTimeline | null> => {
      if (!opts?.silent) setLoading(true);
      try {
        const res = (await rpcClient.labs.levelAssessmentCandidateGet({
          candidateId,
        })) as CandidateTimeline;
        setData(res);
        setError(null);
        return res;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro ao carregar o candidato.");
        return null;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [candidateId],
  );

  useEffect(() => {
    if (candidateId) void load();
  }, [candidateId, load]);

  // Poll while any round's score is still in flight — either running
  // (`evaluating`) OR flipped `ready` but its evaluation hasn't landed yet
  // (`pending`, a slow background score still settling). Stop as soon as none
  // are: a `pending` round that crosses the grace window becomes `failed`
  // server-side, which drops it out of this predicate and terminates the poll.
  const anyInFlight =
    data?.rounds.some((r) => r.outcome === "evaluating" || r.outcome === "pending") ?? false;
  useEffect(() => {
    if (!anyInFlight) return;
    const token = { cancelled: false };
    pollRef.current = token;
    const id = setInterval(() => {
      void (async () => {
        const res = await load({ silent: true });
        if (token.cancelled || !res) return;
        if (!res.rounds.some((r) => r.outcome === "evaluating" || r.outcome === "pending"))
          clearInterval(id);
      })();
    }, POLL_MS);
    return () => {
      token.cancelled = true;
      clearInterval(id);
    };
  }, [anyInFlight, load]);

  const disciplineLabel =
    data && model
      ? (model.disciplines.find((d) => d.id === data.candidate.disciplineId)?.label ?? null)
      : null;

  // Archive / reactivate — reversible, so one click is enough (no confirm).
  const setCandidateStatus = useCallback(
    async (status: "active" | "archived") => {
      setStatusBusy(true);
      try {
        await rpcClient.labs.levelAssessmentCandidateSetStatus({ candidateId, status });
        await load({ silent: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro ao atualizar o status do candidato.");
      } finally {
        setStatusBusy(false);
      }
    },
    [candidateId, load],
  );

  // Undo the last round — destructive and irreversible, so it confirms first
  // (unlike archive, which is a reversible one-click).
  const undoLastRound = useCallback(async () => {
    if (!undoTarget) return;
    setUndoBusy(true);
    setUndoError(null);
    try {
      await rpcClient.labs.levelAssessmentCandidateUndoRound({
        candidateId,
        roundId: undoTarget.id,
      });
      setUndoTarget(null);
      await load({ silent: true });
    } catch (e) {
      setUndoError(e instanceof Error ? e.message : "Erro ao desfazer o round.");
      // The timeline may have moved on (e.g. a round added elsewhere) — refresh
      // so the surface behind the error is current.
      void load({ silent: true });
    } finally {
      setUndoBusy(false);
    }
  }, [candidateId, undoTarget, load]);

  return (
    <div className="ci-stripe ci-stripe-page">
      <CandidateKeyframes />
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1120 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <Link
            href="/labs/level-assessment/candidates"
            style={{
              ...MONO,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 13.5,
              color: C.ink50,
              textDecoration: "none",
              letterSpacing: "0.02em",
            }}
          >
            <ChevronLeft size={15} /> Candidatos
          </Link>
          {data &&
            (data.candidate.status === "active" ? (
              <GhostButton
                disabled={statusBusy}
                onClick={() => void setCandidateStatus("archived")}
              >
                <Archive size={15} /> {statusBusy ? "Arquivando…" : "Arquivar"}
              </GhostButton>
            ) : (
              <GhostButton disabled={statusBusy} onClick={() => void setCandidateStatus("active")}>
                <ArchiveRestore size={15} /> {statusBusy ? "Reativando…" : "Reativar"}
              </GhostButton>
            ))}
        </div>

        {error && <Muted>Falha ao carregar o candidato: {error}</Muted>}
        {loading && !data && <TimelineSkeleton />}

        {data && (
          <>
            <TimelineHeader
              data={data}
              disciplineLabel={disciplineLabel}
              fromRound={effectiveFrom(data)}
              anyInFlight={anyInFlight}
            />

            <LensSwitcher lens={lens} onChange={setLens} />

            {lens === "overview" && <OverviewLens data={data} />}
            {lens === "convergence" && <ConvergenceLens data={data} />}
            {lens === "evidence" && <EvidenceLens data={data} />}

            {lens === "rounds" && (
              <>
                <div
                  style={{
                    ...MONO,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0",
                    textTransform: "uppercase",
                    color: C.ink50,
                    margin: "0 0 4px 2px",
                  }}
                >
                  Rounds · do mais antigo ao mais recente
                </div>

                <div style={{ marginTop: 14 }}>
                  {(() => {
                    let lastScoredLevel: number | null = null;
                    let lastScoredSeq: number | null = null;
                    return data.rounds.map((round, i) => {
                      const isLast = i === data.rounds.length - 1;
                      const node = (
                        <RoundRow
                          key={round.id}
                          round={round}
                          last={isLast}
                          levelNames={data.levelNames}
                          lastScoredLevel={lastScoredLevel}
                          lastScoredSeq={lastScoredSeq}
                          // Only the LAST round can be undone, never round 1 (the
                          // candidate's baseline), and only while active.
                          onUndo={
                            isLast && round.sequence > 1 && data.candidate.status === "active"
                              ? () => {
                                  setUndoError(null);
                                  setUndoTarget(round);
                                }
                              : undefined
                          }
                        />
                      );
                      // Advance the carry-forward anchor AFTER rendering this round.
                      if (
                        round.currentEvaluation &&
                        round.currentEvaluation.overallLevel !== null
                      ) {
                        lastScoredLevel = round.currentEvaluation.overallLevel;
                        lastScoredSeq = round.sequence;
                      }
                      return node;
                    });
                  })()}
                </div>

                {data.candidate.status === "active" && (
                  <div style={{ marginTop: 8, display: "flex" }}>
                    <button
                      type="button"
                      onClick={() => setAddOpen(true)}
                      className="la-addround"
                      style={{
                        width: "100%",
                        border: `1px dashed ${C.ink20}`,
                        background: C.white,
                        borderRadius: 14,
                        padding: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 9,
                        ...UI,
                        color: C.ink50,
                        fontWeight: 500,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      <Plus size={16} /> Adicionar round
                    </button>
                  </div>
                )}
                {data.candidate.status === "archived" && (
                  <div
                    style={{
                      marginTop: 8,
                      border: `1px dashed ${C.ink20}`,
                      borderRadius: 14,
                      padding: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 9,
                      ...UI,
                      color: C.ink50,
                      fontSize: 14,
                    }}
                  >
                    <Archive size={15} />
                    Candidato arquivado — reative o monitoramento para adicionar novos rounds.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {addOpen && data && (
        <AddRoundPanel
          candidateId={data.candidate.id}
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            void load({ silent: true });
          }}
        />
      )}

      {undoTarget && (
        <ModalShell onClose={() => !undoBusy && setUndoTarget(null)}>
          <OverlayHeader
            title={`Desfazer o round ${undoTarget.sequence}?`}
            subtitle="O round, as evidências adicionadas nele e a avaliação que ele produziu serão removidos permanentemente. O candidato volta ao estado do round anterior."
            onClose={() => !undoBusy && setUndoTarget(null)}
          />
          {undoError && <ErrorText>{undoError}</ErrorText>}
          <OverlayActions>
            <GhostButton disabled={undoBusy} onClick={() => setUndoTarget(null)}>
              Cancelar
            </GhostButton>
            <PrimaryButton disabled={undoBusy} onClick={() => void undoLastRound()}>
              {undoBusy ? "Desfazendo…" : "Desfazer round"}
            </PrimaryButton>
          </OverlayActions>
        </ModalShell>
      )}
    </div>
  );
}

/** The case-file lenses: the same case, three projections. */
type CaseLens = "overview" | "rounds" | "convergence" | "evidence";

const LENSES: { id: CaseLens; label: string }[] = [
  { id: "overview", label: "Visão geral" },
  { id: "rounds", label: "Rodadas" },
  { id: "convergence", label: "Convergência" },
  { id: "evidence", label: "Evidências" },
];

function LensSwitcher({ lens, onChange }: { lens: CaseLens; onChange: (l: CaseLens) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Visões do candidato"
      style={{
        display: "inline-flex",
        gap: 4,
        background: C.ink5,
        borderRadius: 999,
        padding: 4,
        margin: "0 0 14px",
      }}
    >
      {LENSES.map((l) => {
        const on = l.id === lens;
        return (
          <button
            key={l.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(l.id)}
            style={{
              ...UI,
              fontSize: 13,
              fontWeight: 600,
              color: on ? C.ink : C.ink50,
              background: on ? C.white : "transparent",
              border: on ? `1px solid ${C.ink10}` : "1px solid transparent",
              borderRadius: 999,
              padding: "6px 14px",
              cursor: "pointer",
            }}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

export function TimelineHeader({
  data,
  disciplineLabel,
  fromRound,
  anyInFlight,
}: {
  data: CandidateTimeline;
  disciplineLabel: string | null;
  fromRound: number | null;
  anyInFlight: boolean;
}) {
  const { candidate, currentEvaluation, levelNames, levels, rounds } = data;
  const lastSeq = rounds.length ? rounds[rounds.length - 1].sequence : null;
  const carried =
    currentEvaluation &&
    currentEvaluation.overallLevel !== null &&
    fromRound !== null &&
    fromRound !== lastSeq &&
    !anyInFlight;

  // A single dense band: identity + meta on the left, current level on the
  // right, and a full-width rail (whisker · sparkline · confidence).
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.ink10}`,
        borderRadius: 18,
        padding: "20px 24px",
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        {/* Identity */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, minWidth: 0 }}>
          <Avatar name={candidate.name} size={48} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h2
                style={{
                  ...DM,
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.ink,
                  lineHeight: 1.2,
                }}
              >
                {candidate.name}
              </h2>
              {disciplineLabel && <DisciplineChip label={disciplineLabel} />}
              <CandidateStatusChip status={candidate.status} />
            </div>
            <div style={{ ...MONO, fontSize: 11.5, color: C.ink50, marginTop: 7 }}>
              {[
                candidate.subtitle,
                candidate.modelId,
                `${rounds.length} round${rounds.length === 1 ? "" : "s"}`,
                `desde ${fmtDate(candidate.createdAt)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        </div>

        {/* Current level */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <div
            style={{
              ...MONO,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0",
              textTransform: "uppercase",
              color: C.red,
            }}
          >
            Nível atual
          </div>
          <CurrentLevelBig ev={currentEvaluation} levelNames={levelNames} />
          {anyInFlight && (
            <div
              style={{
                ...MONO,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                letterSpacing: "0.03em",
                color: C.amberFg,
                textTransform: "uppercase",
                marginTop: 4,
              }}
            >
              <span
                className="la-anim"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: C.amberFg,
                  animation: "la-pulse 1.4s ease-in-out infinite",
                }}
              />
              Atualizando…
            </div>
          )}
          {carried && (
            <div style={{ ...MONO, fontSize: 11, color: C.violet, marginTop: 2 }}>
              nível herdado do Round {fromRound}
            </div>
          )}
        </div>
      </div>

      {/* Rail */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${C.ink10}`,
        }}
      >
        {/* The whisker is fluid — it absorbs the rail's free width, so the
         *  sparkline · confidence · dispersion chips stay pinned to the right
         *  edge of the card at any viewport width. */}
        <div style={{ flex: "1 1 260px", minWidth: 220 }}>
          <LevelWhisker
            mean={currentEvaluation?.overallMean ?? null}
            stddev={currentEvaluation?.overallStddev ?? null}
            levels={levels}
            size="lg"
            width={360}
            fluid
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            marginLeft: "auto",
          }}
        >
          <TrajectorySparkline rounds={rounds} levels={levels} />
          <ConfidenceChip value={currentEvaluation?.overallConfidence ?? null} />
        </div>
      </div>
    </div>
  );
}

function CurrentLevelBig({
  ev,
  levelNames,
}: {
  ev: RoundEvaluationSummary | null;
  levelNames: Record<string, string>;
}) {
  if (ev && ev.overallLevel !== null) {
    const name = levelNames[String(ev.overallLevel)]?.trim();
    return (
      <div
        style={{
          ...DM,
          fontWeight: 700,
          fontSize: 38,
          lineHeight: 1,
          letterSpacing: "-0.02em",
          color: C.ink,
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        L{ev.overallLevel}
        {name && <span style={{ fontSize: 22, fontWeight: 600, color: C.ink70 }}>· {name}</span>}
      </div>
    );
  }
  if (ev && ev.overallLevel === null) {
    return (
      <>
        <div style={{ ...DM, fontWeight: 700, fontSize: 38, lineHeight: 1, color: C.ink20 }}>—</div>
        <div style={{ ...UI, fontSize: 13, color: C.ink50, marginTop: 6 }}>
          Sem nível estimável com a evidência atual
        </div>
      </>
    );
  }
  return (
    <div style={{ ...DM, fontWeight: 700, fontSize: 38, lineHeight: 1, color: C.ink20 }}>—</div>
  );
}

export function RoundRow({
  round,
  last,
  levelNames,
  lastScoredLevel,
  lastScoredSeq,
  onUndo,
}: {
  round: CandidateTimelineRound;
  last: boolean;
  levelNames: Record<string, string>;
  lastScoredLevel: number | null;
  lastScoredSeq: number | null;
  /** Present only on the undoable round (the last one, never round 1). */
  onUndo?: () => void;
}) {
  const [, navigate] = useLocation();
  const [hover, setHover] = useState(false);
  // The server hands us the outcome directly — the client never infers "why is
  // the evaluation null" (deliberate carry-forward vs. a score still landing).
  const outcome = round.outcome;

  // The whole round card opens its interview panel; the report link inside
  // stays a secondary action (it stops propagation).
  const openPanel = () =>
    navigate(`/labs/level-assessment/candidates/${round.candidateId}/rounds/${round.id}`);

  return (
    <div
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: "44px 1fr",
        gap: 16,
        paddingBottom: 16,
      }}
    >
      {/* Spine: node + connector line */}
      <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
        <RoundNode outcome={outcome} sequence={round.sequence} />
        {!last && (
          <div
            style={{
              position: "absolute",
              top: 34,
              bottom: -16,
              width: 2,
              background: C.ink10,
            }}
          />
        )}
      </div>

      {/* Round card — click to open its interview panel */}
      {/* biome-ignore lint/a11y/useSemanticElements: the card is a click target that WRAPS a secondary link (the report link), so it can't be a <button>; keyboard access is provided via tabIndex + onKeyDown. */}
      <div
        className="la-anim"
        role="button"
        tabIndex={0}
        aria-label={`Abrir painel de entrevista do round ${round.sequence}`}
        onClick={openPanel}
        onKeyDown={(e) => {
          // Only the card itself activates; let a focused descendant (the report
          // link) receive its own Enter/Space instead of opening the panel.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPanel();
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          background: C.white,
          border: `1px solid ${hover ? C.ink20 : C.ink10}`,
          borderRadius: 18,
          padding: "18px 20px",
          animation: "la-fadeup .4s ease both",
          cursor: "pointer",
          boxShadow: hover ? "0 4px 14px rgba(31,27,23,0.08)" : "none",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ ...MONO, fontSize: 12, color: C.ink50, letterSpacing: "0.03em" }}>
              Round {round.sequence}
            </span>
            {round.label && (
              <span style={{ ...DM, fontWeight: 600, fontSize: 16, color: C.ink }}>
                {round.label}
              </span>
            )}
            <span style={{ ...MONO, fontSize: 12, color: C.ink50 }}>
              {fmtDate(round.createdAt)}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {onUndo && (
              <button
                type="button"
                aria-label={`Desfazer o round ${round.sequence}`}
                title="Remove o round, suas evidências e sua avaliação"
                onClick={(e) => {
                  // Secondary action inside the clickable card — don't open the panel.
                  e.stopPropagation();
                  onUndo();
                }}
                style={{
                  ...MONO,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 12,
                  color: C.ink50,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <Undo2 size={13} /> Desfazer
              </button>
            )}
            <RoundStatusChip status={round.status} outcome={outcome} />
          </div>
        </div>

        {outcome === "scored" && round.currentEvaluation ? (
          // Scored: keep it compact — evidence + level + confidence + report link
          // all on one row (no divider), instead of a separate result block.
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {round.packetsAdded.map((p) => (
                <PacketChip key={p.id} type={p.type} label={p.label} linkedinUrl={p.linkedinUrl} />
              ))}
              <LevelPill level={round.currentEvaluation.overallLevel} names={levelNames} red />
              <span style={{ ...MONO, fontSize: 12, color: C.ink50, display: "flex", gap: 12 }}>
                <span>
                  Cross{" "}
                  <b style={{ color: C.ink70, fontWeight: 500 }}>
                    {lvl(round.currentEvaluation.crossLevel)}
                  </b>
                </span>
                <span>
                  Vertical{" "}
                  <b style={{ color: C.ink70, fontWeight: 500 }}>
                    {lvl(round.currentEvaluation.verticalLevel)}
                  </b>
                </span>
              </span>
              <ConfidenceChip value={round.currentEvaluation.overallConfidence} />
              <MovementChip movement={round.movement} />
            </div>
            <ReportLink
              evaluationId={round.currentEvaluation.evaluationId}
              label="Avaliação Completa"
            />
          </div>
        ) : (
          <>
            {round.packetsAdded.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0 4px" }}>
                {round.packetsAdded.map((p) => (
                  <PacketChip
                    key={p.id}
                    type={p.type}
                    label={p.label}
                    linkedinUrl={p.linkedinUrl}
                  />
                ))}
              </div>
            )}
            <RoundResult
              outcome={outcome}
              round={round}
              levelNames={levelNames}
              lastScoredLevel={lastScoredLevel}
              lastScoredSeq={lastScoredSeq}
            />
          </>
        )}
      </div>
    </div>
  );
}

function RoundNode({ outcome, sequence }: { outcome: RoundOutcome; sequence: number }) {
  const base: React.CSSProperties = {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    ...MONO,
    fontSize: 13,
    fontWeight: 500,
    zIndex: 1,
    position: "relative",
    flex: "0 0 auto",
  };
  if (outcome === "scored") {
    return <div style={{ ...base, background: C.red, color: C.white }}>{sequence}</div>;
  }
  // Score still in flight (running, or flipped ready and settling) — same
  // pulsing amber ring in both cases.
  if (outcome === "evaluating" || outcome === "pending") {
    return (
      <div
        className="la-anim"
        style={{
          ...base,
          background: C.white,
          color: C.amberFg,
          border: `2px solid ${C.amberFg}`,
          animation: "la-ring 1.6s ease-in-out infinite",
        }}
      >
        {sequence}
      </div>
    );
  }
  if (outcome === "abstained") {
    return (
      <div
        style={{ ...base, background: C.white, color: C.ink50, border: `2px dashed ${C.ink20}` }}
      >
        {sequence}
      </div>
    );
  }
  if (outcome === "failed") {
    // Incomplete score — a static amber dashed ring (attention, not alarm).
    return (
      <div
        style={{
          ...base,
          background: C.white,
          color: C.amberFg,
          border: `2px dashed ${C.amberFg}`,
        }}
      >
        {sequence}
      </div>
    );
  }
  // carry-forward
  return (
    <div style={{ ...base, background: C.white, color: C.ink50, border: `2px solid ${C.ink20}` }}>
      {sequence}
    </div>
  );
}

function RoundResult({
  outcome,
  round,
  levelNames,
  lastScoredLevel,
  lastScoredSeq,
}: {
  outcome: RoundOutcome;
  round: CandidateTimelineRound;
  levelNames: Record<string, string>;
  lastScoredLevel: number | null;
  lastScoredSeq: number | null;
}) {
  const divider: React.CSSProperties = {
    marginTop: 14,
    paddingTop: 14,
    borderTop: `1px solid ${C.ink10}`,
  };

  // Score in flight — running (`evaluating`) or flipped ready and still settling
  // (`pending`). Same skeleton; the copy differs so a settling round doesn't
  // read as if extraction is just starting.
  if (outcome === "evaluating" || outcome === "pending") {
    return (
      <div style={divider}>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <Skeleton style={{ width: "70%", height: 14 }} />
          <Skeleton style={{ width: "45%", height: 12 }} />
        </div>
        <div style={{ ...UI, fontSize: 13, color: C.ink50, marginTop: 10, lineHeight: 1.45 }}>
          {outcome === "pending"
            ? "Finalizando a pontuação deste round — o resultado aparece automaticamente assim que o cálculo terminar."
            : "Extraindo e pontuando o perfil."}{" "}
          O nível atual segue exibindo{" "}
          {lastScoredLevel !== null ? (
            <b>
              L{lastScoredLevel} (Round {lastScoredSeq})
            </b>
          ) : (
            "a última leitura"
          )}{" "}
          até a conclusão.
        </div>
      </div>
    );
  }

  // The scored case is rendered compactly inline by RoundRow (see above), so
  // RoundResult handles only in-flight / abstention / failed / carry-forward.

  if (outcome === "abstained" && round.currentEvaluation) {
    const e = round.currentEvaluation;
    return (
      <div style={divider}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Info size={18} style={{ color: C.ink50, marginTop: 1, flex: "0 0 auto" }} />
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.45 }}>
            <b>Abstenção.</b> A evidência é insuficiente para estimar um nível com segurança — o
            modelo se absteve em vez de arriscar uma leitura frágil. Adicione um perfil mais
            completo ou uma nova evidência para destravar a estimativa.
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <ResultRow evaluationId={e.evaluationId} label="Ver diagnóstico">
            <LevelPill level={null} />
            <ConfidenceChip value={e.overallConfidence} />
          </ResultRow>
        </div>
      </div>
    );
  }

  // Scorable round whose score never landed within the grace window (the
  // background scoring timed out or died before persisting). NOT a
  // carry-forward — surface it honestly so it isn't mistaken for the deliberate
  // fusion-phase message.
  if (outcome === "failed") {
    return (
      <div style={divider}>
        <div
          style={{
            background: C.amberBg,
            borderRadius: 14,
            padding: "13px 15px",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <Info size={18} style={{ color: C.amberFg, marginTop: 1, flex: "0 0 auto" }} />
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.45 }}>
            <b>Pontuação não concluída.</b> O cálculo deste round expirou antes de gravar o
            resultado. Atualize a página para recarregar; se o nível não aparecer, refaça a
            avaliação do candidato.
          </div>
        </div>
      </div>
    );
  }

  // Fusion round: evidence was extracted and recorded, but nothing routed to a
  // level-relevant axis — a neutral "proof of work" card, deliberately NOT the
  // violet carry-forward strip (no level was carried; nothing moved).
  if (outcome === "no_new_signal") {
    const n = round.claimsCount ?? 0;
    return (
      <div style={divider}>
        <div
          style={{
            background: C.ink5,
            border: `1px solid ${C.ink10}`,
            borderRadius: 14,
            padding: "13px 15px",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <Info size={18} style={{ color: C.ink50, marginTop: 1, flex: "0 0 auto" }} />
          <div>
            <div style={{ ...UI, fontSize: 13.5, fontWeight: 600, color: C.ink70 }}>
              Evidências registradas
            </div>
            <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.45, marginTop: 4 }}>
              {n > 0
                ? `${n} observações extraídas — nenhuma altera o nível.`
                : "Nenhuma observação nova relevante."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // carry-forward — a DELIBERATE text-only round, NOT a failure. (Only reached
  // when `outcome === "carried"`, since legacy is the only mode still able to
  // fall through here.)
  return (
    <div style={divider}>
      <div
        style={{
          background: VIOLET_BG,
          border: `1px solid ${VIOLET_BD}`,
          borderRadius: 14,
          padding: "13px 15px",
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <Info size={18} style={{ color: C.violet, marginTop: 1, flex: "0 0 auto" }} />
        <div style={{ flex: 1 }}>
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.45 }}>
            Processando novas evidências
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              marginTop: 10,
              flexWrap: "wrap",
            }}
          >
            {lastScoredSeq !== null && (
              <span
                style={{
                  ...MONO,
                  fontSize: 11,
                  letterSpacing: "0.03em",
                  color: C.violet,
                  background: C.white,
                  border: `1px solid ${VIOLET_BD}`,
                  borderRadius: 999,
                  padding: "3px 9px",
                }}
              >
                herdado do Round {lastScoredSeq}
              </span>
            )}
            <LevelPill level={lastScoredLevel} names={levelNames} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Deep-link to the full report at `/labs/level-assessment/e/:evaluationId`.
 *  `stopPropagation` keeps it a secondary action inside the clickable card. */
function ReportLink({ evaluationId, label }: { evaluationId: string; label: string }) {
  return (
    <Link
      href={`/labs/level-assessment/e/${evaluationId}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        color: C.red,
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      {label} <ArrowUpRight size={14} />
    </Link>
  );
}

/** The abstention result row: left-side content + the report deep-link. */
function ResultRow({
  evaluationId,
  label,
  children,
}: {
  evaluationId: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {children}
      </div>
      <ReportLink evaluationId={evaluationId} label={label} />
    </div>
  );
}

function lvl(n: number | null): string {
  return n === null ? "—" : `L${n}`;
}

function TimelineSkeleton() {
  return (
    <>
      <div
        style={{
          background: C.white,
          border: `1px solid ${C.ink10}`,
          borderRadius: 18,
          padding: "20px 24px",
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 20 }}>
          <div style={{ display: "flex", gap: 14 }}>
            <Skeleton style={{ width: 48, height: 48, borderRadius: 13 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <Skeleton style={{ width: 220, height: 20 }} />
              <Skeleton style={{ width: 300, height: 13 }} />
            </div>
          </div>
          <Skeleton style={{ width: 120, height: 40 }} />
        </div>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.ink10}` }}>
          <Skeleton style={{ width: "100%", height: 32 }} />
        </div>
      </div>
      <Skeleton style={{ width: 140, height: 12, margin: "0 0 16px 2px" }} />
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{ display: "grid", gridTemplateColumns: "44px 1fr", gap: 16, paddingBottom: 16 }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Skeleton style={{ width: 34, height: 34, borderRadius: "50%" }} />
          </div>
          <div
            style={{
              background: C.white,
              border: `1px solid ${C.ink10}`,
              borderRadius: 18,
              padding: "18px 20px",
            }}
          >
            <Skeleton style={{ width: "50%", height: 16, marginBottom: 14 }} />
            <Skeleton style={{ width: "80%", height: 30 }} />
          </div>
        </div>
      ))}
    </>
  );
}
